"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PricingRuleComplementary = void 0;
const class_validator_1 = require("class-validator");
const cloneModelObject_1 = require("../../../../../apis/spg/util/cloneModelObject");
/**
 * Complementary Pricing Rule Base Class: All complementary rules should
 * extend from this and implement its abstract methods
 *
 * The main purpose of this class is to run any custom pricing logic in
 * the apply function. It's used in the base class because it can be shared
 * across all different pricing rules. This currently includes:
 *
 *  - What items are complementary
 *  - How many of that item is complementary
 *  - And the extra price on items based on their complementary status
 */
class PricingRuleComplementary {
    constructor(data) {
        if (data) {
            this.quantity = data.quantity;
        }
    }
    filterSensitiveData() {
        const clone = (0, cloneModelObject_1.cloneModelObject)(this);
        return clone;
    }
    serialize() {
        return {
            name: this.name,
            quantity: this.quantity
        };
    }
    /**
     * Apply any logic for the pricing rules based on the availability and
     * selection of products
     *
     *  - calculate complementary product count
     *  - calculate extra price for product
     */
    apply(products, productAvailability, selectedProducts) {
        // find the complementary product for this pricing rule
        const complementaryProduct = selectedProducts.find((selectedProduct) => this.matchesPricingRule(selectedProduct.product) &&
            this.quantity <= selectedProduct.quantity &&
            selectedProduct.quantity > 0);
        // update all product availabilities with complementary details
        productAvailability.map((pa) => {
            // find the selection for this product availability
            const productSelection = selectedProducts.find((selectedProduct) => selectedProduct.product.productId === pa.productId);
            // find the associated product for this product availability
            const product = products.find((product) => product.productId === pa.productId);
            // only update complementary details if the product matches the
            // pricing rule
            // otherwise other pricing rules will override the data
            if (this.matchesPricingRule(product)) {
                // update this products complementary count
                pa.complementaryCount = this._calculateProductComplementaryCount(productSelection, complementaryProduct, pa);
                // update this products extra price
                pa.extraPrice = this._calculateProductExtraPrice(productSelection, pa);
            }
        });
        // go through an update each product that is now considered extra for
        // the next selection
        this._updateProductsNowExtra(products, productAvailability);
    }
    /**
     * Calculate the complementary count for this product availability based
     * on the complementary product and its current selection
     */
    _calculateProductComplementaryCount(productSelection, complementaryProduct, productAvailability) {
        if (productSelection) {
            // if the product is selected check that it matches the complementary one
            // if it does then the complementary count is just the quantity (note
            // that doesn't support complementary pricing rules with quantity
            // greater than 1
            // if the product is not the complementary one then its count is 0
            return complementaryProduct &&
                productAvailability.productId === complementaryProduct.product.productId
                ? this.quantity
                : 0;
        }
        else {
            // product is not selected so there is nothing complementary
            return 0;
        }
    }
    /**
     * Update all products that are now considered extra and will incur a
     * price for the next selection
     */
    _updateProductsNowExtra(products, productAvailabilities) {
        // extras will only be considered for products that match the same
        // pricing rule. this is important for category rules as we need to
        // update all of them based on the complementary selection in that
        // category. for single items its easy because there should only be one
        // rule that it matches to
        const productAvailabilitiesForProductsMatchingPricingRule = products
            .map((product) => this.matchesPricingRule(product)
            ? productAvailabilities.find((productAvailability) => productAvailability.productId === product.productId)
            : null)
            .filter(sp => !!sp);
        // update now extra state
        productAvailabilities.map((productAvailability) => {
            // if the product is part of the set of other products that match this
            // pricing rule we update its now extra state
            const productAvailabilityIsInProductsMatchingPricingRule = productAvailabilitiesForProductsMatchingPricingRule.findIndex((pa) => pa.productId === productAvailability.productId) !== -1;
            if (productAvailabilityIsInProductsMatchingPricingRule) {
                // product is considered now extra if there's another product where
                // it's complementary account has already reached the complementary
                // count of this rule
                productAvailability.isNowExtra = productAvailabilitiesForProductsMatchingPricingRule.some((pa) => pa.complementaryCount >= this.quantity);
            }
        });
    }
    /**
     * Calculate the extra price of a product based on whether it is a
     * complementary product or not
     */
    _calculateProductExtraPrice(productSelection, productAvailability) {
        if (productSelection) {
            const { quantity, product: { retailPrice } } = productSelection;
            const { complementaryCount } = productAvailability;
            // default the quantity to what's selected
            let extraQuantity = quantity;
            if (complementaryCount > 0) {
                // if the products complementary count is greater than zero we get the
                // extra from what's selected minus how many are considered
                // complementary
                // the extra quantity might go negative so we fix that to zero
                extraQuantity = quantity - complementaryCount;
                extraQuantity = Math.max(0, extraQuantity);
            }
            // price is just the extra quantity multiplied by the price for a
            // single item
            return extraQuantity * retailPrice;
        }
        else {
            // if the product isn't selected then it has no extra price
            return 0;
        }
    }
}
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], PricingRuleComplementary.prototype, "name", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsNotEmpty)(),
    (0, class_validator_1.IsInt)(),
    (0, class_validator_1.Min)(0)
], PricingRuleComplementary.prototype, "quantity", void 0);
exports.PricingRuleComplementary = PricingRuleComplementary;
//# sourceMappingURL=PricingRuleComplementary.js.map